import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { PageNodeBody } from '@/shared/api/generated/model';
import {
  getPagesGetQueryKey,
  getPagesTreeQueryKey,
  pagesCreate,
  pagesDelete,
  pagesMove,
  usePagesTree,
} from '@/shared/api/generated/pages/pages';
import { ApiError } from '@/shared/api/http';
import { unwrap } from '@/shared/api/unwrap';
import { useChannel } from '@/shared/realtime';
import { t } from '@/shared/i18n';
import { toast } from '@/shared/ui/Toast';

/** Уровень страниц: организация (space пуст) или проект. */
export interface PageScope {
  workspace: string;
  /** Slug проекта; пусто — страницы организации. */
  space?: string | undefined;
  /** id организации — для живых обновлений по каналу workspace. */
  workspaceID?: string | undefined;
}

function params(scope: PageScope) {
  return scope.space === undefined || scope.space === '' ? {} : { space: scope.space };
}

export function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.detail : t.common.unknownError;
}

/**
 * Дерево страниц уровня и действия над ним. Любое событие `page.*` по
 * каналу организации перечитывает дерево и открытую страницу — правки
 * коллег видны без перезагрузки.
 */
export function usePages(scope: PageScope) {
  const queryClient = useQueryClient();
  const key = getPagesTreeQueryKey(scope.workspace, params(scope));
  const query = usePagesTree(scope.workspace, params(scope), {
    query: { select: unwrap, enabled: scope.workspace !== '' },
  });

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: key }),
    [queryClient, key],
  );

  useChannel(
    scope.workspaceID === undefined ? undefined : `workspace:${scope.workspaceID}`,
    (event) => {
      if (!event.event.startsWith('page.')) {
        return;
      }

      void refresh();

      const pageID = (event.data as { page_id?: string } | undefined)?.page_id;

      if (pageID !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: getPagesGetQueryKey(scope.workspace, pageID),
        });
      }
    },
  );

  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  const create = useCallback(
    async (parentID?: string): Promise<PageNodeBody | undefined> => {
      try {
        const created = unwrap(
          await pagesCreate(
            scope.workspace,
            { title: '', ...(parentID === undefined ? {} : { parent_id: parentID }) },
            params(scope),
          ),
        );

        await refresh();

        return created;
      } catch (cause) {
        toast.error(describe(cause));

        return undefined;
      }
    },
    [scope, refresh],
  );

  const move = useCallback(
    async (id: string, parentID: string | null, afterID: string | null) => {
      try {
        await pagesMove(scope.workspace, id, {
          ...(parentID === null ? {} : { parent_id: parentID }),
          ...(afterID === null ? {} : { after_id: afterID }),
        });
      } catch (cause) {
        toast.error(describe(cause));
      } finally {
        await refresh();
      }
    },
    [scope.workspace, refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await pagesDelete(scope.workspace, id);
      toast.success(t.pages.deleted);
      await refresh();
    },
    [scope.workspace, refresh],
  );

  return { query, items, refresh, create, move, remove };
}
